import { doc, getDoc, setDoc, FieldValue } from "firebase/firestore";
import { deleteField, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";

const knownCategories = [
    "billing",
    "billingDetails",
    "complianceModules",
    "features",
    "general",
    "integrations",
    "menus",
    "productOverview"
]

export const getCompanySettings = async (companyID: string = window.companyID, category: string = 'all') => { 
    if (!companyID) {
        throw new Error('Company ID is required');
    }

    if (!category || !knownCategories.includes(category) && category !== 'all') {
        throw new Error('Invalid category');
    }

    if (category === 'all') {
        let settings = {};
        for (const cat of knownCategories) {
            const settingsDoc = await getDoc(doc(db, 'customers', companyID, 'companySettings', cat));
            settings[cat] = settingsDoc.data();
        }
        return settings;
    }

    const settingsRef = doc(db, 'customers', companyID, 'companySettings', category);
    const settingsDoc = await getDoc(settingsRef);

    return settingsDoc.data();
};

export const saveCompanySettings = async (companyID: string = window.companyID, category: string, data: any) => { 
    if (!companyID) {
        throw new Error('Company ID is required');
    }

    if (!category || !knownCategories.includes(category) || category === 'all') {
        throw new Error('Invalid category');
    }

    const settingsRef = doc(db, 'customers', companyID, 'companySettings', category);

    try {
        await setDoc(settingsRef, data, { merge: true });
    } catch (error) {
        console.error('Error saving company settings:', error);
        throw error;
    }
}

export const getCompanyInfo = async (companyID: string = window.companyID) => {
    if (!companyID) {
        throw new Error('Company ID is required');
    }

    const companyRef = doc(db, 'customers', companyID);
    const companyDoc = await getDoc(companyRef);

    return companyDoc.data();
}

interface UpdateOperation {
    [key: string]: any | typeof deleteField | null;
    $merge?: boolean; // Optional flag to control merge behavior for specific fields
};

interface UpdateOperations {
    set?: UpdateOperation & {
        $merge?: boolean; // Global merge flag for all set operations
    };
    update?: UpdateOperation;
    arrayUnion?: UpdateOperation;
    arrayRemove?: UpdateOperation;
    merge?: boolean; // Global merge flag for the entire operation
};

/**
 * Saves company information with support for nested field updates
 * @param companyID - The ID of the company (defaults to window.companyID)
 * @param operations - Operations to perform (set, update, arrayUnion, arrayRemove)
 * @param options - Additional options
 */
export const saveCompanyInfo = async (
  companyId: string, 
  operations: UpdateOperations & { merge?: boolean } = { merge: true }
) => { 
    if (!companyId) {
        throw new Error('Company ID is required');
    }

    const companyRef = doc(db, 'customers', companyId);
    console.log('Saving company info for ID:', companyId, 'with operations:', operations);
    const updateData: Record<string, any> = {};

    // Process set operations (overwrites fields)
    if (operations.set) {
        for (const [key, value] of Object.entries(operations.set)) {
            if (key === '$merge') continue; // Skip the merge flag
            
            // Handle field deletions (null or deleteField)
            if (value === null || value === deleteField) {
                if (key.includes('.')) {
                    // For nested deletes, we need to use dot notation with update
                    const keys = key.split('.');
                    const lastKey = keys.pop()!;
                    let current = updateData;
                    
                    for (const k of keys) {
                        current[k] = current[k] || {};
                        current = current[k];
                    }
                    
                    current[lastKey] = deleteField();
                } else {
                    // For top-level deletes
                    updateData[key] = deleteField();
                }
                continue;
            }
            
            // Handle nested field updates
            if (key.includes('.')) {
                // Handle nested fields using dot notation
                const keys = key.split('.');
                const lastKey = keys.pop()!;
                let current = updateData;
                
                for (const k of keys) {
                        // Always create nested objects for the update path
                    current[k] = current[k] || {};
                    current = current[k];
                }
                
                current[lastKey] = value;
            } else {
                updateData[key] = value;
            }
        }
    }

    // Process update operations (merges objects)
    if (operations.update) {
        for (const [key, value] of Object.entries(operations.update)) {
            if (key.includes('.')) {
                // Handle nested updates with dot notation
                const keys = key.split('.');
                const lastKey = keys.pop()!;
                let current = updateData;
                
                // Build the update path
                for (const k of keys) {
                    current[k] = current[k] || {};
                    current = current[k];
                }
                
                // For updates, we want to merge objects but handle null values as deletions
                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    // Initialize the target object if it doesn't exist
                    current[lastKey] = current[lastKey] || {};
                    
                    // Process each field in the update object
                    Object.entries(value).forEach(([fieldKey, fieldValue]) => {
                        if (fieldValue === null) {
                            // Use FieldValue.delete() for Firestore field deletions
                            current[lastKey][fieldKey] = deleteField();
                        } else {
                            // Handle field updates
                            current[lastKey][fieldKey] = fieldValue;
                        }
                    });
                } else {
                    // For non-object values, just set the value
                    current[lastKey] = value;
                }
            } else {
                // Handle top-level updates
                if (value === null) {
                    updateData[key] = deleteField();
                } else {
                    updateData[key] = value;
                }
            }
        }
    }

    // Process array operations
    if (operations.arrayUnion) {
        for (const [key, value] of Object.entries(operations.arrayUnion)) {
            updateData[key] = {
                ...updateData[key],
                [key]: {
                    ...updateData[key]?.[key],
                    [key === 'businessUnitNames' ? 'businessUnitNames' : key]: {
                        ...updateData[key]?.[key]?.[key === 'businessUnitNames' ? 'businessUnitNames' : key],
                        ...Object.fromEntries(
                            (Array.isArray(value) ? value : [value]).map((item: any) => {
                                if (typeof item === 'object' && item !== null && 'id' in item) {
                                    return [item.id, item];
                                }
                                const id = typeof item === 'string' ? item : JSON.stringify(item);
                                return [id, { id, name: item }];
                            })
                        )
                    }
                }
            };
        }
    }

    try {
        if (Object.keys(updateData).length > 0) {
            const merge = operations.merge ?? true;
            await setDoc(companyRef, updateData, { merge });
        }
        
        // If we have arrayRemove operations, handle them in a separate update
        if (operations.arrayRemove) {
            const removeData: Record<string, any> = {};
            
            for (const [key, idsToRemove] of Object.entries(operations.arrayRemove)) {
                const ids = Array.isArray(idsToRemove) ? idsToRemove : [idsToRemove];
                
                if (key.includes('.')) {
                    // Handle nested map deletions (e.g., customerOrg.teams.businessUnitNames)
                    const keys = key.split('.');
                    const lastKey = keys.pop()!;
                    let current = removeData;
                    
                    // Build the update path
                    for (const k of keys) {
                        current[k] = current[k] || {};
                        current = current[k];
                    }
                    
                    // Initialize the target object if it doesn't exist
                    current[lastKey] = current[lastKey] || {};
                    
                    // Add field deletes for each ID
                    ids.forEach((id: string) => {
                        current[lastKey][id] = deleteField();
                    });
                } else {
                    // Handle top-level array removes
                    removeData[key] = {
                        ...removeData[key],
                        ...ids.reduce((acc: Record<string, any>, id: string) => {
                            acc[id] = deleteField();
                            return acc;
                        }, {})
                    };
                }
            }
            
            if (Object.keys(removeData).length > 0) {
                console.log('Applying arrayRemove operations:', removeData);
                await setDoc(companyRef, removeData, { merge: true });
            }
        }
        
        return true;
    } catch (error) {
        console.error('Error saving company info:', error);
        throw error;
    }
}

export default {
    getCompanySettings,
    getCompanyInfo,
    saveCompanySettings,
    saveCompanyInfo
}